import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { usePageMeta } from '@/hooks/usePageMeta';
import { TableOfContents } from '@/components/content/TableOfContents';
import { ArticleFeedback } from '@/components/content/ArticleFeedback';

const ARTICLE_DESCRIPTION =
  "Sport Fund and Spotfund NIL have similar-sounding names but serve different athletes. Spotfund NIL is built for college NIL collectives; Sport Fund is built for individual youth and amateur athletes. Here's the honest difference.";

const FAQS = [
  {
    q: 'Is Sport Fund the same company as Spotfund or Spotfund NIL?',
    a: "No. Sport Fund (thesportfund.com) and Spotfund (spotfund.com, including its spotfund.com/nil product) are separate, unaffiliated companies with similar-sounding names. Sport Fund is not endorsed by, sponsored by, or connected to Spotfund in any way.",
  },
  {
    q: 'Who is Spotfund NIL built for?',
    a: "Spotfund NIL is built for college Name, Image and Likeness (NIL) collectives — it advertises partnerships with 100+ NIL collectives and cites a single college football campaign that raised $2.5M. It's aimed at collegiate athletic programs and boosters running large, organized NIL campaigns.",
  },
  {
    q: 'Who is Sport Fund built for?',
    a: "Sport Fund is built for individual youth and amateur athletes across 30+ sports — a gymnast covering meet travel, a swimmer covering club dues, a high schooler covering private coaching. Each athlete owns their own page; there's no collective, collegiate program, or booster structure involved.",
  },
  {
    q: 'How do the fees compare?',
    a: "Spotfund advertises 0% platform fee, funded by donor tips that can be adjusted down to zero, plus standard card processing (reported around 2.9% + $0.30). Sport Fund charges no fee to the athlete at all — the athlete keeps 100% of every donation — and adds a transparent, non-optional fee (~5% bank, ~7% card) on top, paid by the supporter and shown before checkout.",
  },
  {
    q: 'If I searched for "sport fund" and found Spotfund instead (or vice versa), what should I do?',
    a: 'Check the URL. Sport Fund is only at thesportfund.com. If a page is asking about NIL collectives, college boosters, or a "drive" for a collegiate program, it is very likely Spotfund, not Sport Fund. If you\'re raising money for one individual athlete\'s personal costs — travel, gear, coaching — thesportfund.com is the athlete-owned option.',
  },
];

const SpotfundVsSportFund: React.FC = () => {
  usePageMeta(
    'Sport Fund vs Spotfund NIL: Not the Same Platform (2026)',
    ARTICLE_DESCRIPTION,
    {
      article: true,
      image: '/resources/athlete-training.jpg',
      breadcrumbs: [
        { name: 'Resources', path: '/resources' },
        { name: 'Sport Fund vs Spotfund NIL' },
      ],
      jsonLd: {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    }
  );
  const TOC_SECTIONS = [
    {
      id: "different-athletes-different-problem",
      text: "Different athletes, different problem"
    },
    {
      id: "an-honest-comparison",
      text: "An honest comparison"
    },
    {
      id: "how-to-tell-them-apart-fast",
      text: "How to tell them apart fast"
    },
    {
      id: "who-should-consider-sport-fund",
      text: "Who should consider Sport Fund"
    },
    {
      id: "start-in-minutes",
      text: "Start in minutes"
    },
    {
      id: "frequently-asked-questions",
      text: "Frequently asked questions"
    },
    {
      id: "read-next",
      text: "Read next"
    }
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Link to="/resources" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Resources
      </Link>
      <h1 className="text-3xl md:text-4xl font-bold mb-4">
        Sport Fund vs Spotfund NIL: similar names, different platforms
      </h1>
      <p className="text-gray-500 mb-8 text-lg">
        If a search for "sport fund" turned up Spotfund (or Spotfund NIL), you're not imagining the
        similarity — but the two aren't the same company, and they don't serve the same athletes.
      </p>

      <article className="prose prose-lg max-w-none">
        <TableOfContents sections={TOC_SECTIONS} />
        <p>
          The names are close enough to cause real confusion: <strong>Sport Fund</strong>{' '}
          (thesportfund.com) and <strong>Spotfund</strong> (spotfund.com), which also runs a
          product called <strong>Spotfund NIL</strong> at spotfund.com/nil. They are unrelated
          companies. If you landed here after searching for one and finding the other, here's what
          actually separates them.
        </p>

        <h2 id="different-athletes-different-problem">Different athletes, different problem</h2>
        <p>
          Spotfund NIL is built for <strong>college Name, Image and Likeness (NIL) collectives</strong>{' '}
          — the page advertises partnerships with "100+ NIL Collectives" and cites a single college
          football campaign that raised a reported $2.5M. It's a tool for collegiate athletic
          programs and boosters running large, organized fundraising drives.
        </p>
        <p>
          Sport Fund is built for the opposite end of the spectrum: <strong>one individual youth or
          amateur athlete</strong>, across 30+ sports, covering their own personal costs — travel to
          a meet, club dues, gear, private coaching. There's no collective, no collegiate program, and
          no booster structure. Each athlete creates and owns a single page shared with their own
          family and friends.
        </p>

        <h2 id="an-honest-comparison">An honest comparison</h2>
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th></th>
                <th>Spotfund / Spotfund NIL</th>
                <th>Sport Fund</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Built for</strong></td>
                <td>General crowdfunding, plus a dedicated NIL product for college collectives</td>
                <td><strong>One individual youth/amateur athlete's own page</strong></td>
              </tr>
              <tr>
                <td><strong>Typical campaign size</strong></td>
                <td>Reports $50M+ raised platform-wide; a single NIL campaign cited at $2.5M</td>
                <td><strong>Season-sized: travel, dues, gear, coaching for one athlete</strong></td>
              </tr>
              <tr>
                <td><strong>Platform fee</strong></td>
                <td>0% platform fee, funded by adjustable donor tips (can be set to $0), plus processing (~2.9% + $0.30)</td>
                <td><strong>$0 to the athlete — a transparent, non-optional supporter fee (~5% bank / ~7% card) shown before checkout</strong></td>
              </tr>
              <tr>
                <td><strong>Recurring monthly giving</strong></td>
                <td>Available for nonprofit campaigns by default; individual fundraisers must email Spotfund support to turn it on</td>
                <td><strong>Built-in and self-serve from day one — no support request needed</strong></td>
              </tr>
              <tr>
                <td><strong>Payout</strong></td>
                <td>To the campaign organizer/collective</td>
                <td><strong>Straight to the athlete/family bank</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-gray-500">
          Figures for Spotfund reflect its own publicly published site content and third-party review
          sites as of 2026 and may change or vary by agreement — confirm current terms directly with
          Spotfund. Sport Fund is not affiliated with, endorsed by, or sponsored by Spotfund or
          Spotfund NIL. The name is used only to compare services (nominative reference).
        </p>

        <h2 id="how-to-tell-them-apart-fast">How to tell them apart fast</h2>
        <ul>
          <li><strong>Check the URL.</strong> Sport Fund is only ever at <strong>thesportfund.com</strong>.</li>
          <li><strong>Check who the page is for.</strong> Language about NIL collectives, college boosters, or a program-wide "drive" belongs to Spotfund NIL, not Sport Fund.</li>
          <li><strong>Check the scale.</strong> If you're raising money for one athlete's own season costs — not a collegiate collective — Sport Fund is the athlete-owned option built for that.</li>
        </ul>

        <h2 id="who-should-consider-sport-fund">Who should consider Sport Fund</h2>
        <p>
          Individual youth and amateur athletes and their families across 30+ sports who want an
          ongoing, recurring page funded by the people who already know them — not a collegiate NIL
          collective and not a general crowdfunding page.
        </p>

        <h2 id="start-in-minutes">Start in minutes</h2>
        <p>
          Setting up a fund page is free and takes a few minutes. Tell your story, set a target, and
          share it with your own network.
        </p>
        <p>
          <Link to="/signup-creator" className="text-blue-600">Start a free fund page</Link> — keep 100% of every donation.
        </p>

        <h2 id="frequently-asked-questions">Frequently asked questions</h2>
        <div className="not-prose space-y-6">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="font-semibold text-lg text-gray-900 mb-1">{f.q}</h3>
              <p className="text-gray-600">{f.a}</p>
            </div>
          ))}
        </div>

        <h2 id="read-next">Read next</h2>
        <p>
          <Link to="/resources/snap-raise-alternatives" className="text-blue-600">Snap! Raise Alternatives in 2026</Link> — how Sport Fund compares to another well-known team fundraising platform.
        </p>
        <p>
          <Link to="/resources/drive-sports-and-team-fundraisers" className="text-blue-600">Drive Sports & Team Fundraising Apps</Link> — where team-drive platforms leave the individual athlete out.
        </p>
        <ArticleFeedback articleSlug="spotfund-vs-sport-fund" />
      </article>
    </div>
  );
};

export default SpotfundVsSportFund;
